import React, { useState, useEffect, useContext } from "react";
import api, { reportsApi } from "../../../../API/axiosInstance";
import { toast } from "react-toastify";
import { Pagination, Tooltip, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close"; // can remove if unused elsewhere
import AssessmentIcon from "@mui/icons-material/Assessment"; // can remove if unused
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

import "./../../../../styles/FakeNews.css";
import NavBar from "../../../Admin/Dashboard/NavBar";
import ReactProSidebar from "../../../Admin/Dashboard/ReactProSidebar";
import { generateHash } from "../../../../API/hashUtil";
import { MyContext } from "../../../../MyContextApi";
import PrintFactCheck from "../../../GeneratePdf/PrintFactCheck";
import FactCheckCard from "./FactCheckCard";
// import BasicDateTimeRangePicker from "../../../Filters/DateTimeRange"; // ❌ not needed now
import DateRangeModalPicker from "../../../Filters/GlobalDateRange"; // ✅ new
import dayjs from "dayjs";
import FactCarousel from "./FactCarousel";
import CustomAlertDialog from "../../../Dialog";
import FactCheckCardSkeleton from "./FactSkeleton";
const tempUrl = `http://10.226.37.205:8081`;

const normalizeReportsPath = (path = "") => {
  const value = path || "";
  return /^https?:\/\//i.test(value)
    ? value
    : value.replace(/^\/?reports(?=\/)/, "");
};

const FactChecker = () => {
  const [data, setData] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [fromDateTimeFormatted, setFromDateTimeFormatted] = useState(null);
  const [toDateTimeFormatted, setToDateTimeFormatted] = useState(null);
  const [clearDates, setClearDates] = useState(false);
  const [factCheckServerresponse, setFactCheckServerresponse] = useState(null);
  const [factLoading, setFactLoading] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState("");
  const [dialogConfig, setDialogConfig] = useState({
    open: false,
    title: "",
    message: "",
    iconType: "info",
    onClose: null,
  });
  const [searchTerm, setSearchTerm] = useState("");

  // NEW: show inline report controls + date modal
  const [showReportControls, setShowReportControls] = useState(false);
  const [dateModalOpen, setDateModalOpen] = useState(false);

  const { selectedId, setSelectedId, dialogOpen, setDialogOpen } =
    useContext(MyContext);

  const fetchData = async (page) => {
    setCardLoading(true);

    try {
      const stringParams = page ? page.toString() : "";
      const hexHash = generateHash(stringParams);

      const token = localStorage.getItem("Token");
      const response = await api.post(
        `/fakenews/getFactCheckData`,
        { page },
        {
          params: { HPPcode: hexHash },
          headers: {
            "X-XSS-Protection": "1; mode=block",
            Authorization: `Bearer ${token}`,
            "X-Content-Type-Options": "nosniff",
            "X-Frame-Options": "DENY",
            "Referrer-Policy": "strict-origin-when-cross-origin",
            "Permissions-Policy": "camera=(), microphone=(), geolocation=()",
            "Content-Type": "application/json",
          },
        }
      );

      const parsedData =
        typeof response.data === "string"
          ? JSON.parse(response.data)
          : response.data;

      if (parsedData && parsedData.data && Array.isArray(parsedData.data)) {
        const sortedData = parsedData.data.sort(
          (a, b) => new Date(b.datetime) - new Date(a.datetime)
        );

        setData(sortedData);
        setCurrentPage(parsedData.current_page);
        setTotalPages(parsedData.total_pages);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching fake news data:", error);
      setData([]);
    } finally {
      setCardLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage]);

  const updateFactCheckRecords = async () => {
    setIsUpdating(true);
    setUpdateMessage("Initiating fact-check records update...");

    try {
      const token = localStorage.getItem("Token");

      const stringParams = {
        action: "update-fact-check-records",
        timestamp: Date.now().toString(),
      };
      const hexHash =
        typeof generateHash !== "undefined" ? generateHash(stringParams) : "";

      const response = await api.post(
        `/fakenews/update-fact-check-records`,
        {},
        {
          params: {
            HPPcode: hexHash,
          },
          headers: {
            "X-XSS-Protection": "1; mode=block",
            Authorization: `Bearer ${token}`,
            "X-Content-Type-Options": "nosniff",
            "X-Frame-Options": "DENY",
            "Referrer-Policy": "strict-origin-when-cross-origin",
            "Permissions-Policy": "camera=(), microphone=(), geolocation=()",
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status === 200) {
        setUpdateMessage(
          "Update initiated successfully! This may take a few minutes to complete."
        );

        if (typeof toast !== "undefined") {
          toast.success("Fact-check records update started! 🚀");
        }

        setTimeout(() => {
          setIsUpdating(false);
          setUpdateMessage("Update completed! ✅");

          if (typeof toast !== "undefined") {
            toast.success("Fact-check records updated successfully! 🎉");
          }
        }, 30000);
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (error) {
      console.error("Error updating fact-check records:", error);
      setIsUpdating(false);
      setUpdateMessage("Failed to initiate update. Please try again.");

      if (typeof toast !== "undefined") {
        toast.error("Failed to update fact-check records. Please try again.");
      } else {
        setDialogConfig({
          open: true,
          title: "Update Error",
          message: "Failed to update fact-check records. Please try again.",
          iconType: "error",
          onClose: () => setDialogConfig((prev) => ({ ...prev, open: false })),
        });
      }
    }

    const resetUpdate = () => {
      setIsUpdating(false);
      setUpdateMessage("");
    };
    return {
      isUpdating,
      updateMessage,
      updateFactCheckRecords,
      resetUpdate,
    };
  };

  const generateFactcheck = async () => {
    try {
      setFactLoading(true);

      const token = localStorage.getItem("Token");

      const startDateStr = fromDateTimeFormatted
        ? fromDateTimeFormatted.toString()
        : "";

      const endDateStr = toDateTimeFormatted
        ? toDateTimeFormatted.toString()
        : "";

      const stringParams = {
        startDateStr,
        endDateStr,
        type: "fact",
      };

      const hexHash = generateHash(stringParams);

      const response = await reportsApi.post(
        `/fact-check`,
        {
          startDate: startDateStr,
          endDate: endDateStr,
          type: "fact",
        },
        {
          params: {
            HPPcode: hexHash,
          },
          
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // ✅ Extract filename from headers
      if (!response.data.success) throw new Error(response.data.message);

      const fileRes = await reportsApi.get(
        normalizeReportsPath(response.data.download_url),

        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );

      const blob = new Blob([fileRes.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = response.data.filename || "CSMART_FactCheck_Report.pdf";
      document.body.appendChild(a);
      a.click();

      // cleanup (IMPORTANT)
      a.remove();
      URL.revokeObjectURL(url);

      // const cd = response.headers["content-disposition"];
      // let filename =
      //   cd?.match(/filename="?([^"]+)"?/)?.[1] ||
      //   `CSMART_Fact_Check_Report_${new Date().toISOString().slice(0, 10)}.pdf`;

      // // ✅ Create blob and download
      // const blob = new Blob([response.data], {
      //   type: "application/pdf",
      // });

      // const url = window.URL.createObjectURL(blob);

      // const a = document.createElement("a");
      // a.href = url;
      // a.download = filename;
      // document.body.appendChild(a);
      // a.click();
      // a.remove();

      // window.URL.revokeObjectURL(url);

      // ✅ Reset UI
      setShowReportControls(false);
      setFromDateTimeFormatted(null);
      setToDateTimeFormatted(null);
      setSearchTerm("");
      setFactLoading(false);
      setClearDates(true);
      setTimeout(() => setClearDates(false), 0);
    } catch (error) {
      console.error("Error downloading fact check report:", error);
      toast.error("Failed to download fact-check report.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setFactLoading(false);
    }
  };

  const handleFromDateTimeChange = (fromDateTime) => {
    if (!fromDateTime) {
      setFromDateTimeFormatted(null);
      return;
    }
    const formattedFromDateTime = dayjs(fromDateTime).format(
      "YYYY-MM-DDTHH:mm:ss[Z]"
    );
    setFromDateTimeFormatted(formattedFromDateTime);
  };

  const handleToDateTimeChange = (toDateTime) => {
    if (!toDateTime) {
      setToDateTimeFormatted(null);
      return;
    }
    const formattedToDateTime = dayjs(toDateTime).format(
      "YYYY-MM-DDTHH:mm:ss[Z]"
    );
    setToDateTimeFormatted(formattedToDateTime);
  };

  return (
    <div className="container">
      <NavBar />
      <CustomAlertDialog
        open={dialogConfig.open}
        onClose={dialogConfig.onClose}
        title={dialogConfig.title}
        message={dialogConfig.message}
        iconType={dialogConfig.iconType}
      />

      {/* Global Date Range Modal */}
      <DateRangeModalPicker
        open={dateModalOpen}
        onClose={() => setDateModalOpen(false)}
        onFromDateTimeChange={handleFromDateTimeChange}
        onToDateTimeChange={handleToDateTimeChange}
        clearDates={clearDates}
      />

      <main>
        <div className="fake-news-container">
          <div className="fact-header-container">
            <h1 className="basic-Header">Fact Checker</h1>

            <div className="fn-report-button-section">
              {!showReportControls ? (
                // STEP 1: initial big button
                <button
                  className="fn-top-button"
                  disabled={factLoading}
                  onClick={() => setShowReportControls(true)}
                >
                  {factLoading ? (
                    <>
                      Generating Report...
                      <div className="loading-dots">
                        <span className="dot"></span>
                        <span className="dot"></span>
                        <span className="dot"></span>
                      </div>
                    </>
                  ) : (
                    <>Generate Report</>
                  )}
                </button>
              ) : (
                // STEP 2: inline controls (calendar + search + download)
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    background: "white",
                    borderRadius: "30px",
                    padding: "10px 17px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <Tooltip title="Select Date Range" arrow>
                    <IconButton
                      size="small"
                      onClick={() => setDateModalOpen(true)}
                      disabled={factLoading}
                      sx={{
                        borderRadius: "50%",
                        backgroundColor: "#f0f4ff",
                        color: "#1976d2",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: "#e3eaff",
                          transform: "scale(1.05)",
                        },
                        "&:disabled": {
                          backgroundColor: "#f5f5f5",
                          color: "#bdbdbd",
                        },
                      }}
                    >
                      <CalendarMonthIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* Search input inline */}
                  {/* <input
                    type="text"
                    placeholder="Search term…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={factLoading}
                    style={{
                      minWidth: "180px",
                      maxWidth: "240px",
                      padding: "6px 10px",
                      borderRadius: "20px",
                      border: "1px solid #ddd",
                      fontSize: "13px",
                      outline: "none",
                      fontFamily: "'Host Grotesk', sans-serif",
                    }}
                  /> */}

                  <Button
                    onClick={generateFactcheck}
                    variant="contained"
                    size="small"
                    disabled={
                      factLoading ||
                      !fromDateTimeFormatted ||
                      !toDateTimeFormatted ||
                      fromDateTimeFormatted === "Invalid Date" ||
                      toDateTimeFormatted === "Invalid Date"
                    }
                    sx={{
                      borderRadius: "50px",
                      fontFamily: "'Host Grotesk', sans-serif",
                      fontSize: "13px",
                      fontWeight: 600,
                      textTransform: "none",
                      padding: "8px 24px",
                      minWidth: "160px",
                      background: factLoading
                        ? "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)"
                        : "linear-gradient(180deg, #4079f0 0%, #2a5fe6 100%)",
                      boxShadow: factLoading
                        ? "none"
                        : "0 4px 12px rgba(53, 114, 239, 0.35)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: factLoading
                          ? "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)"
                          : "linear-gradient(135deg, #2a5fe6 0%, #1d4ed8 100%)",
                        boxShadow: factLoading
                          ? "none"
                          : "0 6px 16px rgba(53, 114, 239, 0.45)",
                        transform: factLoading ? "none" : "translateY(-1px)",
                      },
                      "&:active": {
                        transform: factLoading ? "none" : "translateY(0px)",
                      },
                      "&:disabled": {
                        background:
                          "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)",
                        color: "#9e9e9e",
                      },
                    }}
                  >
                    {factLoading ? "Generating..." : "Download Report"}
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="fake-news-top">
            <FactCarousel />
            <div className="fact-check-wrapper">
              <button
                onClick={updateFactCheckRecords}
                disabled={isUpdating}
                className="fn-report-button"
              >
                {isUpdating ? (
                  <>
                    Retrieving fresh data...
                    <div className="loading-dots">
                      <span className="dot"></span>
                      <span className="dot"></span>
                      <span className="dot"></span>
                    </div>
                  </>
                ) : (
                  "Update Records"
                )}
              </button>
            </div>
          </div>

          <div className="fake-news-cards-section">
            {data.map((item) =>
              cardLoading ? (
                <FactCheckCardSkeleton key={item._id} count={4} />
              ) : (
                <FactCheckCard key={item._id} data={item} />
              )
            )}
          </div>

          {cardLoading ? (
            ""
          ) : (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(event, value) => setCurrentPage(value)}
              color="primary"
              sx={{
                marginBottom: "13px",
                marginTop: "10px",
                marginRight: "30px",
                display: "flex",
                justifyContent: "center",
                "& .MuiPaginationItem-root": {
                  backgroundColor: "#E8F2FF",
                  color: "#1A365D",
                  border: "1px solid #B8D4F0",
                  "&:hover": {
                    backgroundColor: "#D1E7FF",
                    borderColor: "#3572EF",
                  },
                },
                "& .Mui-selected": {
                  backgroundColor: "#3572EF",
                  color: "#FFFFFF",
                  borderRadius: "50%",
                  fontWeight: "bold",
                  border: "1px solid #3572EF",
                  "&:hover": {
                    backgroundColor: "#2563EB",
                  },
                },
                "& .MuiPaginationItem-previousNext": {
                  backgroundColor: "#F0F7FF",
                  color: "#3572EF",
                  "&:hover": {
                    backgroundColor: "#E0EFFF",
                  },
                },
                "& .MuiPaginationItem-firstLast": {
                  backgroundColor: "#F0F7FF",
                  color: "#3572EF",
                  "&:hover": {
                    backgroundColor: "#E0EFFF",
                  },
                },
              }}
              showFirstButton
              showLastButton
            />
          )}
        </div>
      </main>
      <ReactProSidebar />
    </div>
  );
};

export default FactChecker;
